import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Loader from "components/Loader/loader";

import inquiriesTableData from "layouts/inquiries/data/inquiriesTableData";
import { getInquiries, getInquiryById } from "services/apiService";

const STATUS_OPTIONS = [
  { value: "", label: "All", icon: "list_alt" },
  { value: "open", label: "Open", icon: "mark_email_unread" },
  { value: "closed", label: "Closed", icon: "check_circle" },
];

// The global theme hides the native select arrow, so re-enable it per filter field.
const FILTER_SELECT_PROPS = {
  IconComponent: ArrowDropDownIcon,
  sx: {
    "& .MuiSelect-icon": { display: "block", right: 8, color: "text.secondary" },
    "& .MuiSelect-select": { paddingRight: "32px !important" },
  },
};

const optionContent = (icon, label) => (
  <MDBox display="flex" alignItems="center" gap={1}>
    <Icon fontSize="small" sx={{ color: "text.secondary" }}>
      {icon}
    </Icon>
    <MDTypography variant="button" fontWeight="regular">
      {label}
    </MDTypography>
  </MDBox>
);

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : undefined;

const mapInquiry = (inquiry) => ({
  id: inquiry?.id,
  name: inquiry?.fullName,
  email: inquiry?.email,
  phone: inquiry?.phone,
  message: inquiry?.message,
  status: inquiry?.status,
  submittedAt: formatDate(inquiry?.createdAt),
  listingTitle: inquiry?.listing?.title,
  listingMake: inquiry?.listing?.make,
  listingModel: inquiry?.listing?.model,
  listingYear: inquiry?.listing?.year,
  sellerName: inquiry?.listing?.seller?.fullName,
  sellerPhone: inquiry?.listing?.seller?.phone,
});

function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewInquiry, setViewInquiry] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await getInquiries({
        status: status || undefined,
        search: search || undefined,
        page,
        limit: pageCount,
      });
      if (response?.success) {
        setInquiries((response?.data?.inquiries || []).map(mapInquiry));
        setTotalInquiries(response?.data?.total ?? 0);
        setTotalPages(response?.data?.totalPages ?? 1);
      }
    } catch (err) {
      console.error("Fetch inquiries failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageCount, status]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchInquiries();
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleView = async (inquiry) => {
    setViewOpen(true);
    setViewInquiry(inquiry);
    try {
      const response = await getInquiryById(inquiry?.id);
      if (response?.success) {
        setViewInquiry(mapInquiry(response?.data?.inquiry || response?.data));
      }
    } catch (err) {
      console.error("Fetch inquiry failed:", err);
    }
  };

  const { columns, rows } = useMemo(
    () =>
      inquiriesTableData({
        inquiryList: inquiries,
        onView: handleView,
      }),
    [inquiries]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Inquiries
                </MDTypography>
              </MDBox>

              <MDBox px={2} pt={3} display="flex" gap={2} flexWrap="wrap">
                <MDBox width="16rem">
                  <TextField
                    select
                    label="Status"
                    fullWidth
                    size="medium"
                    value={status}
                    onChange={(e) => updateFilter(setStatus)(e.target.value)}
                    SelectProps={FILTER_SELECT_PROPS}
                    sx={{ "& .MuiInputBase-root": { height: 44 } }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {optionContent(opt.icon, opt.label)}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>
                <MDBox width="20rem">
                  <TextField
                    label="Search"
                    placeholder="Search by name, email, phone or message"
                    fullWidth
                    size="medium"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ "& .MuiInputBase-root": { height: 44 } }}
                  />
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={false}
                  page={page}
                  onPageChange={setPage}
                  pageEntryCount={updateFilter(setPageCount)}
                  manualPagination
                  pageCount={totalPages}
                  totalEntries={totalInquiries}
                />
              </MDBox>
            </Card>

            {/* ── View Inquiry Dialog ── */}
            <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>
                Inquiry Details
                <IconButton
                  aria-label="close"
                  onClick={() => setViewOpen(false)}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {[
                  { label: "Name", value: viewInquiry?.name },
                  { label: "Email", value: viewInquiry?.email },
                  { label: "Phone", value: viewInquiry?.phone },
                  { label: "Status", value: viewInquiry?.status },
                  { label: "Submitted", value: viewInquiry?.submittedAt },
                ].map((row) => (
                  <MDBox
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    py={0.75}
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ flexShrink: 0 }}>
                      {row.label}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium" sx={{ textAlign: "right" }}>
                      {row.value || "—"}
                    </MDTypography>
                  </MDBox>
                ))}

                <Divider sx={{ my: 2 }} />

                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Message
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="text">
                  {viewInquiry?.message || "—"}
                </MDTypography>

                <Divider sx={{ my: 2 }} />

                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Listing
                </MDTypography>
                {[
                  {
                    label: "Vehicle",
                    value: viewInquiry?.listingTitle
                      ? viewInquiry.listingTitle
                      : [viewInquiry?.listingYear, viewInquiry?.listingMake, viewInquiry?.listingModel]
                          .filter(Boolean)
                          .join(" "),
                  },
                  { label: "Seller", value: viewInquiry?.sellerName },
                  { label: "Seller Phone", value: viewInquiry?.sellerPhone },
                ].map((row) => (
                  <MDBox
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    py={0.75}
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ flexShrink: 0 }}>
                      {row.label}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium" sx={{ textAlign: "right" }}>
                      {row.value || "—"}
                    </MDTypography>
                  </MDBox>
                ))}
              </DialogContent>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Inquiries;
